import type { SingleComboBox } from "@itandi/itandi-bb-ui/SingleComboBox";
import type { Th } from "@itandi/itandi-bb-ui/Th";
import type { ComponentProps } from "react";
import type { DealType } from "./Deal";
import type { Staff } from "./Staff";
import type { Subtype } from "./Subtype";

export type CompanyDealTodo = {
	completed: boolean;
	completedAt: string | null;
	createdAt: string;
	deal: {
		dealSubtype: Subtype;
		dealType: DealType;
		id: number;
		name: string;
		roomNumber: string | null;
		staff: { id: number; name: string };
	};
	dueDate: string | null;
	id: number;
	memo: string | null;
	originVendorCompany: {
		id: number;
		name: string;
		vendorUser: { id: number; name: string } | null;
	} | null;
	staff: { id: number; name: string } | null;
	storeName: string | null;
	title: string;
};

export type TodoSortKey = "id" | "dueDate";

export const unassignedStaff = { label: "担当者未設定", value: "-1" } as const;

export type CompanyDealTodoSearchCondition = {
	authId?: number | null;
	companyVendorId?: number | null;
	completed?: "true" | "false" | "all";
	dealName?: string;
	dealTypes?: DealType[];
	dueDateFrom?: string;
	dueDateTo?: string;
	page?: number;
	roomNumber?: string;
	sortKey?: TodoSortKey;
	sortOrder?: ComponentProps<typeof Th>["sort"];
	staffId?: number | null;
	text?: string;
};

export const SEARCH_CONDITION_KEYS = [
	"page",
	"authId",
	"completed",
	"text",
	"staffId",
	"dueDateFrom",
	"dueDateTo",
	"dealName",
	"roomNumber",
	"dealTypes",
	"companyVendorId",
	"sortKey",
	"sortOrder"
] as const satisfies readonly (keyof CompanyDealTodoSearchCondition)[];

const COMPLETED_VALUES = [
	"true",
	"false",
	"all"
] as const satisfies readonly Exclude<
	CompanyDealTodoSearchCondition["completed"],
	undefined
>[];

export const isQueryCompleted = (
	query: string
): query is (typeof COMPLETED_VALUES)[number] =>
	COMPLETED_VALUES.some((completedValue) => completedValue === query);

// /** 担当者の並び順を未設定→ログイン担当者→その他に並び替えたSingleComboBoxのOptions */
export const staffOptions = (
	staffs: Staff[],
	loginStaff: Staff | null
): ComponentProps<typeof SingleComboBox>["options"] => {
	const loginStaffId = loginStaff?.id;

	const orderedStaffs = staffs.toSorted((a, b) => {
		if (loginStaffId === undefined) return 0;
		if (a.id === loginStaffId) return -1;
		if (b.id === loginStaffId) return 1;
		return 0;
	});

	return [
		unassignedStaff,
		...orderedStaffs.map((s) => ({ label: s.name, value: String(s.id) }))
	];
};

export const selectedStaffOption = (
	searchCondition: CompanyDealTodoSearchCondition,
	selectedStaff: Staff | null
): ComponentProps<typeof SingleComboBox>["value"] => {
	if (searchCondition.staffId === Number(unassignedStaff.value)) {
		return unassignedStaff;
	}
	return (
		selectedStaff && {
			label: selectedStaff.name,
			value: String(selectedStaff.id)
		}
	);
};
